import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import RootRoutes from 'RootRoutes';
import EnumValuesStorage from 'contexts/EnumValuesStorage';
import MenuInfo from 'data/menu';
import { getBrowserTitle } from 'data/data';
import ThemeCustomization from 'themes';
import { ConfigProvider } from 'contexts/ConfigContext';
import { AlarmProvider } from 'providers/AlarmProvider';
import RouteLogger from 'RouteLogger';
import TechSupportWatermark from 'components/TechSupportWatermark';

export default function App() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const hostname = window.location.hostname;
  const location = useLocation();
  const root = MenuInfo.ROOT;
  root.active(location.pathname);

  const setBWTitle = () => {
    document.title = t(getBrowserTitle(hostname));
    document.querySelector('meta[name="description"]')?.setAttribute('content', t(getBrowserTitle(hostname)));
  };

  setBWTitle();

  useEffect(() => {
    window.__APP_NAVIGATE__ = navigate;
  }, [navigate]);

  useEffect(() => {
    const favicon = document.querySelector("link[rel='icon']");

    if (!favicon) return;

    const isChina = process.env.REACT_APP_TIMEZONE === 'Asia/Shanghai';
    const isNantong = process.env.REACT_APP_PLATFORM_SERVER?.toUpperCase() === 'NANTONG';

    favicon.href = isChina
      ? isNantong ? `${process.env.PUBLIC_URL}/favicon-nt.png` : `${process.env.PUBLIC_URL}/favicon-china.png`
      : `${process.env.PUBLIC_URL}/favicon.png`;
  }, []);

  useEffect(() => {
    if (location.pathname.includes('/login') || location.pathname === '/') {
      resetAllStyles();
      const content = document.querySelector('.monitering');
      if (content && content.parentElement) {
        content.parentElement.removeAttribute('style');
      }
      return;
    }

    const baseWidth = 1920;
    const minWidth = 1280;
    let resizeTimeout = null;
    let rafId = null;
    let isScaling = false; 

    function resetAllStyles() {
      const content = document.querySelector('.monitering');
      if (content) {
        content.style.transform = '';
        content.style.width = '';
        content.style.height = '';
        content.style.transformOrigin = '';
        content.style.willChange = '';
        content.style.position = '';
        content.style.left = '';
        content.style.top = '';
        content.style.overflow = '';
      }
    }

    function applyScale() {
      const content = document.querySelector('.monitering');
      if (!content) return;

      const parent = content.parentElement; 
      if (!parent) return;

      // 1. window.innerWidth로 측정하여 세로 스크롤바 유무에 영향받지 않도록 보정
      // (부모 element 폭 기준을 원할 경우 parent.clientWidth 대신 document.documentElement.clientWidth 사용)
      const viewportWidth = window.innerWidth;

      if (viewportWidth >= minWidth) {
        // 부모 컴포넌트의 가로 폭
        const parentWidth = parent.clientWidth || viewportWidth;
        let scale = Math.floor((parentWidth / baseWidth) * 1000) / 1000;
        
        isScaling = true;

        // 원본 크기로 복원 후 스크롤 높이 측정
        content.style.transform = 'none';
        content.style.width = `${baseWidth}px`;
        content.style.height = 'max-content';
        content.style.overflow = 'visible';

        const originalScrollHeight = content.scrollHeight;
        let realContentHeight = originalScrollHeight < 900 ? 1080 : originalScrollHeight;

        content.style.transform = `scale(${scale})`;
        content.style.transformOrigin = 'top left';
        content.style.position = 'absolute';
        content.style.left = '0px';
        content.style.top = '0px';
        content.style.willChange = 'transform';

        // 하단 잘림 방지 15px 안전 마진 추가
        const calculatedHeight = Math.ceil(realContentHeight * scale) + 15;
        
        parent.style.position = 'relative';
        parent.style.width = '100%'; 
        parent.style.height = `${calculatedHeight}px`; 
        parent.style.overflowX = 'hidden';
        // parent.style.overflowY = 'auto';
        parent.style.overflowY = 'visible';

        rafId = requestAnimationFrame(() => {
          isScaling = false;
        });
      } else {
        isScaling = true;
        resetAllStyles();
        if (parent) {
          parent.removeAttribute('style');
        }
        rafId = requestAnimationFrame(() => {
          isScaling = false;
        });
      }
    }

    function handleResize() {
      if (isScaling) return;
      if (resizeTimeout) clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        if (rafId) cancelAnimationFrame(rafId);
        rafId = requestAnimationFrame(applyScale);
      }, 30); 
    }

    const initTimeout = setTimeout(() => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(applyScale);
    }, 500);

    window.addEventListener('resize', handleResize);

    const observerTarget = document.querySelector('.monitering');
    let resizeObserver = null;

    if (observerTarget) {
      resizeObserver = new ResizeObserver(() => {
        if (isScaling) return; 
        if (rafId) cancelAnimationFrame(rafId);
        rafId = requestAnimationFrame(applyScale);
      });
      resizeObserver.observe(observerTarget);
    }

    return () => {
      clearTimeout(initTimeout);
      if (resizeTimeout) clearTimeout(resizeTimeout);
      window.removeEventListener('resize', handleResize);
      if (rafId) cancelAnimationFrame(rafId);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      resetAllStyles();
    };
  }, [location.pathname]);

  if (location.pathname.startsWith('/auth')) {
    return (
      <ConfigProvider>
        <ThemeCustomization>
          <AlarmProvider>
            <RootRoutes/>
          </AlarmProvider>
        </ThemeCustomization>
      </ConfigProvider>
    );
  } else {
    return (
      <ConfigProvider>
        <ThemeCustomization>
          <EnumValuesStorage>
            <AlarmProvider>
              <RouteLogger/>
              <RootRoutes/>
              <TechSupportWatermark />
            </AlarmProvider>
          </EnumValuesStorage>
        </ThemeCustomization>
      </ConfigProvider>
    );
  }
}